import apiClient from '@u/apiClient'
import PaymentExportController from '@/wayfinder/actions/App/Http/Controllers/Payment/PaymentExportController'
import GetPaymentByIdController from '@/wayfinder/actions/App/Http/Controllers/Payment/GetPaymentByIdController'
import DeletePaymentController from '@/wayfinder/actions/App/Http/Controllers/Payment/DeletePaymentController'
import CheckChequeNumberAvailabilityController from '@/wayfinder/actions/App/Http/Controllers/Payment/CheckChequeNumberAvailabilityController'
import GetPaymentLinkCandidatesController from '@/wayfinder/actions/App/Http/Controllers/Invoice/GetPaymentLinkCandidatesController'
import type { PaymentLinkCandidates } from '@ui/modal/payment/paymentLinkCandidates'
import type { ChequeDuplicate } from '@ui/modal/payment/chequeNumberDuplicate'
import { apiPath } from '@u/apiPath'

const base_errors = {}

function processSuccessCallback(payload) {
  if (payload.successCallback) {
    payload.successCallback()
  }
}

export default {
  // region fetch
  paymentExportExcel({ commit }, payload) {
    return new Promise((resolve, reject) => {
      apiClient
        .route(PaymentExportController.post(), payload)
        .then((response) => {
          resolve(response.data)
        })
        .catch((reason) => {
          reject(reason)
        })
    })
  },
  fetchNewPayment: ({ commit }, payload) => {
    commit('auth/setErrors', base_errors, { root: true })

    return new Promise((resolve, reject) => {
      const params = new URLSearchParams({
        model: 'payment',
        ...payload
      }).toString()

      apiClient
        .get(`/api/model/new?${params}`)
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  // endregion

  fetchPaymentById: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      apiClient
        .route(
          GetPaymentByIdController.get({
            invoice: payload.invoice_id,
            payment: payload.payment_id
          })
        )
        .then(async (response) => {
          resolve(response.data)
        })
        .catch(async (reason) => {
          reject(reason)
        })
    })
  },
  /**
   * The cheque already carrying this number, or null when it is free. Silent
   * on failure: the save refuses a duplicate anyway.
   */
  async checkChequeNumberAvailability(
    _context,
    payload: {
      check_number: string
      payment_mode: string
      ignore_payment_id?: string | null
    }
  ): Promise<ChequeDuplicate | null> {
    const response = await apiClient.route(
      CheckChequeNumberAvailabilityController.get({ query: payload }),
      { skipValidationToast: true, skipNotFoundToast: true }
    )

    return response.data.duplicate ?? null
  },
  /**
   * The documents a payment recorded on this one can also be linked to. A failure is announced once,
   * by the apiClient error hook (a 404 is skipped); the caller adds no toast of its own, since the
   * payment is recorded on its own document anyway.
   */
  async fetchPaymentLinkCandidates(
    _context,
    payload: { invoice_id: string }
  ): Promise<PaymentLinkCandidates> {
    const response = await apiClient.route(
      GetPaymentLinkCandidatesController.get({ invoice: payload.invoice_id }),
      { skipNotFoundToast: true }
    )

    return response.data
  },
  // region store
  storeNewPayment: ({ commit }, payload) => {
    commit('auth/setErrors', base_errors, { root: true })
    return new Promise((resolve, reject) => {
      let url = `invoicing/${payload.invoice_id}/payment`
      if (payload.change_status_dossier === 1) {
        url += `?change_status_dossier=1`
      }
      apiClient
        .post(apiPath(url), payload.payment)
        .then((response) => {
          if (response.status === 200) {
            commit('setInvoice', response.data.invoice)
            commit('setInvoiceMetadata', response.data.metadata)
            resolve(response.data)
          }
        })
        .catch(async (reason) => {
          reject(reason)
        })
    })
  },
  // endregion

  // region update
  updatePaymentById: ({ commit }, payload) => {
    commit('auth/setErrors', base_errors, { root: true })
    return new Promise((resolve, reject) => {
      let url = `invoicing/${payload.invoice_id}/payment/${payload.payment_id}`
      if (payload.change_status_dossier === 1) {
        url += `?change_status_dossier=1`
      }
      apiClient
        .put(apiPath(url), payload.payment)
        .then((response) => {
          commit('setInvoice', response.data.invoice)
          commit('setInvoiceMetadata', response.data.metadata)
          resolve(response.data)
        })
        .catch(async (reason) => {
          reject(reason)
        })
    })
  },
  // endregion

  // region delete
  deletePayment: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      apiClient
        .route(
          DeletePaymentController.delete.delete({
            invoice: payload.invoice_id,
            payment: payload.payment_id
          })
        )
        .then((response) => {
          commit('setInvoice', response.data.invoice)
          commit('setInvoiceMetadata', response.data.metadata)
          resolve(response.data)
        })
        .catch((reason) => {
          reject(reason)
        })
    })
  },

  async undoDeletePayment({ commit }, payload) {
    return new Promise((resolve, reject) => {
      apiClient
        .route(
          DeletePaymentController.undo.get({
            invoice: payload.invoice_id,
            id: payload.payment_id
          })
        )
        .then((response) => {
          commit('setInvoice', response.data.invoice)
          commit('setInvoiceMetadata', response.data.metadata)
          resolve(response.data)
        })
        .catch((reason) => {
          reject(reason)
        })
    })
  }
  // endregion
}
